import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Bell, Plus, Trash2, RefreshCw, MapPin, TrendingUp, Bed, Bath, Maximize2, X, Check, Loader2, Pause, Play, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const VERDICT_STYLE = {
  GO: "bg-emerald-100 text-emerald-700 border-emerald-200",
  MAYBE: "bg-amber-100 text-amber-700 border-amber-200",
  PASS: "bg-rose-100 text-rose-700 border-rose-200",
  UNKNOWN: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Alerts() {
  const nav = useNavigate();
  const [searches, setSearches] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // search.id currently being polled
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", city: "Phoenix", state: "AZ", zip_code: "", price_min: "", price_max: "400000", beds_min: "3" });
  const [expandedGroups, setExpandedGroups] = useState({}); // address → bool

  // Group alerts by listing address — collapse duplicates that fired across
  // multiple saved searches into a single expandable row.  The freshest alert
  // in each group becomes the "primary" displayed when collapsed.
  const grouped = useMemo(() => {
    const byAddr = new Map();
    for (const a of alerts) {
      const addr = a.listing?.address || `__alert_${a.id}`;
      if (!byAddr.has(addr)) byAddr.set(addr, []);
      byAddr.get(addr).push(a);
    }
    return Array.from(byAddr.entries()).map(([addr, rows]) => {
      const sorted = [...rows].sort((x, y) =>
        (y.created_at || "").localeCompare(x.created_at || ""),
      );
      const unreadCount = rows.filter((r) => !r.read).length;
      return { address: addr, rows: sorted, primary: sorted[0], unreadCount };
    });
  }, [alerts]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/saved-searches"),
        api.get("/alerts", { params: { unread_only: unreadOnly } }),
      ]);
      setSearches(s.data || []);
      setAlerts(a.data || []);
    } catch {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  const createSearch = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give it a name");
    if (!form.city && !form.state && !form.zip_code) return toast.error("Specify a city, state, or ZIP");
    try {
      const payload = {
        name: form.name,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        price_min: form.price_min ? Number(form.price_min) : null,
        price_max: form.price_max ? Number(form.price_max) : null,
        beds_min: form.beds_min ? Number(form.beds_min) : null,
      };
      await api.post("/saved-searches", payload);
      toast.success("Saved — polling now…");
      setShowNew(false);
      setForm({ name: "", city: "", state: "", zip_code: "", price_min: "", price_max: "", beds_min: "" });
      setTimeout(refresh, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const runNow = async (s) => {
    setRunning(s.id);
    try {
      const { data } = await api.post(`/saved-searches/${s.id}/run`);
      toast.success(`${data.new_alerts} new alerts`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(null);
    }
  };

  const toggleEnabled = async (s) => {
    await api.patch(`/saved-searches/${s.id}`, { enabled: !s.enabled });
    refresh();
  };

  const deleteSearch = async (s) => {
    if (!window.confirm(`Delete "${s.name}" and all its alerts?`)) return;
    await api.delete(`/saved-searches/${s.id}`);
    toast.success("Deleted");
    refresh();
  };

  const markRead = async (a) => {
    await api.post(`/alerts/${a.id}/read`);
    setAlerts((prev) => prev.map((x) => (x.id === a.id ? { ...x, read: true } : x)));
    window.dispatchEvent(new Event("af:alerts-changed"));
  };

  const markAllRead = async () => {
    await api.post("/alerts/mark-all-read");
    setAlerts((prev) => prev.map((x) => ({ ...x, read: true })));
    window.dispatchEvent(new Event("af:alerts-changed"));
    toast.success("All caught up");
  };

  const deleteAlert = async (a) => {
    await api.delete(`/alerts/${a.id}`);
    setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    window.dispatchEvent(new Event("af:alerts-changed"));
  };

  const addToPipeline = async (a) => {
    try {
      const { data } = await api.post("/discover/add-to-pipeline", {
        address: a.listing.address,
        city: a.listing.city,
        state: a.listing.state,
        zip_code: a.listing.zip_code,
        bedrooms: a.listing.bedrooms,
        bathrooms: a.listing.bathrooms,
        sqft: a.listing.square_footage,
        asking_price: a.listing.price,
        estimated_arv: a.arv,
        notes: `Auto-flagged from saved search "${a.saved_search_name}" — ${a.verdict} (spread ${fmtMoney(a.spread)})`,
      });
      await markRead(a);
      toast.success(data.created ? "Added to pipeline" : "Already in pipeline");
      if (data?.lead?.id) nav(`/app/leads/${data.lead.id}/report`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  return (
    <div data-testid="alerts-page">
      <div className="topbar">
        <div>
          <div className="crumb">Alerts</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Deal Alerts</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Saved searches that find, score, and push new deals to you automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={markAllRead}
            disabled={alerts.every((a) => a.read)}
            data-testid="alerts-mark-all-read-btn"
          >
            <Check size={14} /> Mark all read
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowNew(true)}
            data-testid="alerts-new-search-btn"
          >
            <Plus size={14} /> New Saved Search
          </button>
        </div>
      </div>

      {/* Saved searches list */}
      <div className="card p-4 mb-5" data-testid="saved-searches-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-2">
            <Bell size={16} /> Saved Searches ({searches.length})
          </div>
        </div>
        {searches.length === 0 ? (
          <div className="text-sm text-[var(--ink-2)]">
            No saved searches yet. Save one from the <button type="button" className="underline" onClick={() => nav("/app/discover")}>Discover</button> page or hit “New Saved Search”.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {searches.map((s) => (
              <div key={s.id} className="border rounded-lg p-3 bg-white" data-testid={`saved-search-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{s.name}</div>
                    <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5">
                      <MapPin size={11} />
                      {[s.city, s.state, s.zip_code].filter(Boolean).join(" ")}
                    </div>
                  </div>
                  <span className={"text-[10px] px-1.5 py-0.5 rounded-full font-semibold " + (s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}>
                    {s.enabled ? "ON" : "OFF"}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-2">
                  {s.price_max ? `≤ ${fmtMoney(s.price_max)}` : "any price"} • {s.beds_min ? `${s.beds_min}+ bd` : "any beds"} • every {s.interval_hours}h
                </div>
                <div className="text-[10px] text-[var(--ink-2)] mt-1">
                  {s.last_polled_at ? `Last polled ${new Date(s.last_polled_at).toLocaleString()}` : "Never polled"}
                  {s.last_listing_count != null && ` • ${s.last_listing_count} listings`}
                </div>
                <div className="flex gap-1 mt-2">
                  <button type="button" className="btn btn-ghost text-xs flex-1" onClick={() => runNow(s)} disabled={running === s.id} data-testid={`run-now-${s.id}`}>
                    {running === s.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Run now
                  </button>
                  <button type="button" className="btn btn-ghost text-xs" onClick={() => toggleEnabled(s)} title={s.enabled ? "Pause" : "Resume"}>
                    {s.enabled ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <button type="button" className="btn btn-ghost text-xs text-rose-600" onClick={() => deleteSearch(s)} data-testid={`delete-search-${s.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alerts feed */}
      <div className="flex items-center gap-3 mb-3">
        <h2 className="font-display font-bold text-xl">Alerts feed</h2>
        <label className="text-xs flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="alerts-unread-toggle" />
          Unread only
        </label>
        <span className="text-xs text-[var(--ink-2)]" data-testid="alerts-count">
          {grouped.length} {grouped.length === 1 ? "property" : "properties"} · {alerts.length} alert{alerts.length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <div className="card p-6 text-center text-[var(--ink-2)]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="card p-8 text-center text-[var(--ink-2)]" data-testid="alerts-empty">
          <Bell size={32} className="mx-auto mb-3 text-[var(--primary)]" />
          <div className="font-semibold text-[var(--ink)] mb-1">No alerts yet</div>
          <div className="text-sm">Save a search and we&apos;ll alert you when new GO deals appear.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {grouped.map((g) => {
            const a = g.primary;
            const lst = a.listing || {};
            const hasDuplicates = g.rows.length > 1;
            const isExpanded = !!expandedGroups[g.address];
            return (
              <div
                key={g.address}
                className={"card p-3 " + (g.unreadCount > 0 ? "border-[var(--primary)] shadow-sm" : "opacity-90")}
                data-testid={`alert-group-${g.address.replace(/[^a-zA-Z0-9]/g, "_")}`}
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={"text-[10px] px-2 py-0.5 rounded-full font-bold border " + VERDICT_STYLE[a.verdict]}>{a.verdict}</span>
                      {g.unreadCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--primary)] text-white font-semibold">{g.unreadCount} NEW</span>}
                      {hasDuplicates ? (
                        <button
                          type="button"
                          onClick={() => setExpandedGroups((s) => ({ ...s, [g.address]: !s[g.address] }))}
                          className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-0.5 transition-colors"
                          data-testid={`alert-group-toggle-${g.address.replace(/[^a-zA-Z0-9]/g, "_")}`}
                        >
                          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          matched {g.rows.length} saved searches
                        </button>
                      ) : (
                        <span className="text-xs text-[var(--ink-2)]">from &ldquo;{a.saved_search_name}&rdquo;</span>
                      )}
                    </div>
                    <div className="font-semibold truncate mt-1">{lst.address}</div>
                    <div className="flex items-center gap-3 text-xs text-[var(--ink-2)] mt-1 flex-wrap">
                      {lst.bedrooms != null && <span className="flex items-center gap-1"><Bed size={11} /> {lst.bedrooms}</span>}
                      {lst.bathrooms != null && <span className="flex items-center gap-1"><Bath size={11} /> {lst.bathrooms}</span>}
                      {lst.square_footage != null && <span className="flex items-center gap-1"><Maximize2 size={11} /> {lst.square_footage.toLocaleString()} sqft</span>}
                      {lst.days_on_market != null && <span>{lst.days_on_market} DOM</span>}
                    </div>
                  </div>
                  <div className="flex sm:flex-col items-end justify-between gap-1 sm:min-w-[180px]">
                    <div className="text-right">
                      <div className="font-bold">{fmtMoney(lst.price)}</div>
                      <div className="text-[10px] text-[var(--ink-2)]">ARV {fmtMoney(a.arv)}</div>
                      <div className="text-[10px] text-emerald-700 flex items-center gap-1 justify-end"><TrendingUp size={10} /> spread {fmtMoney(a.spread)}</div>
                    </div>
                    <div className="flex gap-1">
                      {!a.read && (
                        <button type="button" className="btn btn-ghost text-xs" onClick={() => markRead(a)} data-testid={`alert-mark-read-${a.id}`}>
                          <Check size={12} />
                        </button>
                      )}
                      <button type="button" className="btn btn-primary text-xs" onClick={() => addToPipeline(a)} data-testid={`alert-add-pipeline-${a.id}`}>
                        <Plus size={12} /> Pipeline
                      </button>
                      <button type="button" className="btn btn-ghost text-xs text-rose-600" onClick={() => deleteAlert(a)} data-testid={`alert-delete-${a.id}`}>
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                </div>
                {hasDuplicates && isExpanded && (
                  <div className="mt-3 pt-3 border-t border-[var(--line)] flex flex-col gap-1" data-testid={`alert-group-expanded-${g.address.replace(/[^a-zA-Z0-9]/g, "_")}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1">Matched saved searches</div>
                    {g.rows.map((row) => (
                      <div
                        key={row.id}
                        className={"flex items-center justify-between text-xs px-2 py-1.5 rounded-md " + (row.read ? "" : "bg-emerald-50/40")}
                        data-testid={`alert-row-${row.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {!row.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                          <span className="font-medium truncate">{row.saved_search_name}</span>
                          <span className="text-[var(--ink-3)] shrink-0">
                            · {row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}
                          </span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {!row.read && (
                            <button type="button" className="btn btn-ghost p-1" onClick={() => markRead(row)} title="Mark read" data-testid={`alert-mark-read-${row.id}`}>
                              <Check size={10} />
                            </button>
                          )}
                          <button type="button" className="btn btn-ghost p-1 text-rose-600" onClick={() => deleteAlert(row)} title="Delete" data-testid={`alert-delete-${row.id}`}>
                            <X size={10} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New saved search modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setShowNew(false)} data-testid="new-search-modal">
          <form
            onSubmit={createSearch}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg">New Saved Search</div>
              <button type="button" className="btn btn-ghost p-1" onClick={() => setShowNew(false)}>
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2 text-xs text-[var(--ink-2)]">
                Name
                <input className="input mt-1" placeholder="Phoenix 3+bd <400k" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-search-name" />
              </label>
              <label className="text-xs text-[var(--ink-2)]">
                City
                <input className="input mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="new-search-city" />
              </label>
              <label className="text-xs text-[var(--ink-2)]">
                State
                <input className="input mt-1 uppercase" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid="new-search-state" />
              </label>
              <label className="text-xs text-[var(--ink-2)]">
                ZIP
                <input className="input mt-1" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="new-search-zip" />
              </label>
              <label className="text-xs text-[var(--ink-2)]">
                Min Price
                <input className="input mt-1" type="number" value={form.price_min} onChange={(e) => setForm({ ...form, price_min: e.target.value })} data-testid="new-search-price-min" />
              </label>
              <label className="text-xs text-[var(--ink-2)]">
                Max Price
                <input className="input mt-1" type="number" value={form.price_max} onChange={(e) => setForm({ ...form, price_max: e.target.value })} data-testid="new-search-price-max" />
              </label>
              <label className="text-xs text-[var(--ink-2)] col-span-2">
                Min Beds
                <input className="input mt-1" type="number" value={form.beds_min} onChange={(e) => setForm({ ...form, beds_min: e.target.value })} data-testid="new-search-beds-min" />
              </label>
            </div>
            <button type="submit" className="btn btn-primary w-full mt-4" data-testid="new-search-submit">
              <Plus size={14} /> Create &amp; Run
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
